"use client";

import Image from "next/image";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import PlayWhiteBlueIcon from "~/icons/play-white-blue.svg";
import styles from "./HighlightsCarousel.module.scss";
import cn from "clsx";
import Markdown from "@/components/atoms/Markdown";
import addLineBreaks from "@/helpers/addLineBreaks";
import { ModalButton } from "@/components/atoms/ModalButton";
import { IModalActions, ModalTypes } from "@/types/modal";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";

export function HightlightsCard({
  isPinned,
  videoData,
  rootStyles,
  hasSmallTitle = false,
  sectionHeading=""
}: {
  isPinned?: boolean;
  videoData: VideoWithText;
  rootStyles?: string;
  hasSmallTitle?: boolean;
  sectionHeading?: string;
}) {
  const fireWebEngage=()=>{
    window.webengage.track("Video Watched", {
      "Video URL":videoData.URL,
      "Video Title":videoData.Title,
    })
  }

  return (
    <div
      className={cn(
        "border-[1.5px] border-primary-blue rounded-2xl h-full",
        rootStyles
      )}
    >
      <div className={"flex flex-col justify-between h-full"}>
        <div className="border-b-[1.5px] border-primary-blue rounded-2xl overflow-hidden bg-white h-full w-full">
          <img
            src={videoData?.PosterImage?.data?.attributes?.url}
            alt={videoData?.PosterImage?.data?.attributes?.alternativeText ||'Higlights Image'}
            className="object-cover w-full xl:m-auto rounded-2xl h-full"
            loading="lazy"
            title="Higlights Image"
          />
        </div>

        <div
          className={`${
            styles.HighlightsCarousel__TextContainer
          } bg-white text-primary-blue flex items-end justify-between lg:gap-2 rounded-b-2xl ${
            !isPinned ? "lg:items-center" : ""
          }
              `}
        >
          <div>
            <Markdown as="h3"
              className={cn(styles.HighlightsCarousel__Title, {
                [styles.HighlightsCarousel__Title__Pinned]: isPinned,
                [styles.HighlightsCarousel__SmallTitle]: hasSmallTitle,
              })}
            >
              {addLineBreaks(videoData?.Title)}
            </Markdown>
            {!!videoData?.Subtitle && (
              <p
                className={`${styles.HighlightsCarousel__SubTitle} ${
                  !isPinned && "xl:hidden"
                }`}
              >
                {videoData?.Subtitle}
              </p>
            )}
          </div>
          <ModalButton
            action={IModalActions.OpenModal}
            data={{
              modalType: ModalTypes.VideoPopup,
              modalData: { url: videoData.URL },
            }}
            handleClickCB={()=>{
              pushToDataLayer({
                event: "play_video_icon_click",
                cta_text: "Play Video",
                section_name: sectionHeading || "NA",
                page_type: getGlobalStateValue('pageType') || "Home Page",
              });
              fireWebEngage()
            }}
          >
            <Image
              src={PlayWhiteBlueIcon}
              alt="highlights1"
              className={`max-w-none ${
                isPinned ? "h-[61.07px] w-[60.312px]" : "h-[45px] w-[45px]"
              }`}
              loading="lazy"
            />
          </ModalButton>
        </div>
      </div>
    </div>
    
  )
}

export default function HightlightsCarousel({
  videosData,
  sectionHeading=""
}: {
  videosData: VideoWithText[];
  sectionHeading?:string;
}) {
  return (
    <div id="Carousel">
      <Swiper
        modules={[Pagination]}
        spaceBetween={10}
        autoplay={{
          delay: 6000,
        }}
        slidesPerView={"auto"}
        pagination={{ clickable: true, bulletElement: "div" }}
        className="Carousel__blueBullet lg:Carousel__hAuto"
        breakpoints={{
          1024: {
            spaceBetween: 20,
          },
        }}
      >
        {videosData?.map((slideContent, index) => (
          <SwiperSlide key={index}>
            <HightlightsCard videoData={slideContent} sectionHeading={sectionHeading} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
